import {Indexer, Denormalizer} from 'rlink-cms/indexing';
import {processImages} from 'rlink-cms/indexing/images';
import formattedImgPath from 'rlink-repos/formattedImgPath';
import {mergeStaticOptions} from 'rlink-utils/object';
import {getAt} from 'rlink-collections/immutable';


export class ArticlesIndexer extends Indexer {

  itemData (content) {
    return content.data.delete('body').delete('authorPath').set('bannerPath', content.bannerPath).set('bannerImageFormats', content.bannerImageFormats);
  }

  get linkedContentPaths () {
    let pathList = new Set();

    for (let item of this.items) {
      let bannerPath = item.get('bannerImageFormats');
      for (let format of (bannerPath.get('formats') || [{contentType: "image/jpeg", fileExt: "jpg"}])) {
        for ( let width of (bannerPath.get('widths') || []) ) {
          pathList.add(formattedImgPath(bannerPath.get('src'), {width}, format.get('fileExt')));
        }
        for (let height of (bannerPath.get('heights') || [])) {
          pathList.add(formattedImgPath(bannerPath.get('src'), {height}, format.get('fileExt')));
        }
      }

      if (typeof (item.get('body')) !== 'undefined') {
        let matchAll = item.get('body').match(/.*?src="(\/content\/.*?)"/g);
        if (matchAll) {
          for (let i = 0; i < matchAll.length; i++) {
            let match = matchAll[i].match(/.*?src="(\/content\/.*?)"/);
            pathList.add(match[1]);
          }
        }
      }
    }

    return Array.from(pathList);
  }

  get sortKey () {
    return ['publishedOn'];
  }
}

ArticlesIndexer.contentClassName = 'Article';

ArticlesIndexer.translatedAttributes = {
  title: {
    path: ['items'],
    values: ['title'],
    translationSource: {
      contentPath: ['path'],
      translationAttr: 'title',
    },
  },
  introduction: {
    path: ['items'],
    values: ['introduction'],
    translationSource: {
      contentPath: ['path'],
      translationAttr: 'introduction',
    },
  },
};

export class ArticleBySlug extends Denormalizer {
  get linkedContentPaths () {
    let pathList = new Set();
    let bannerPath = this.data.get('bannerImageFormats');
    for (let format of (bannerPath.get('formats') || [{contentType: "image/jpeg", fileExt: "jpg"}])) {
      for ( let width of (bannerPath.get('widths') || []) ) {
        pathList.add(formattedImgPath(bannerPath.get('src'), {width}, format.get('fileExt')));
      }
      for (let height of (bannerPath.get('heights') || [])) {
        pathList.add(formattedImgPath(bannerPath.get('src'), {height}, format.get('fileExt')));
      }
    }

    if (typeof (this.data.get('body')) !== 'undefined') {
      let matchAll = this.data.get('body').match(/.*?src="(\/content\/.*?)"/g);
      if (matchAll) {
        for (let i = 0; i < matchAll.length; i++) {
          let match = matchAll[i].match(/.*?src="(\/content\/.*?)"/);
          pathList.add(match[1]);
        }
      }
    }

    return Array.from(pathList);
  }

  static contentData (content) {
    let bannerImageFormats = (content.bannerImageFormats) ? content.bannerImageFormats : {}; // we get authors in here too
    return content.data.set('bannerPath', content.bannerPath).set('$path', content.path).set('bannerImageFormats', bannerImageFormats);
  }

  static shouldDenormalize (mainContent) {
    return mainContent && mainContent.data && !mainContent.data.get('isExternal');
  }
}

mergeStaticOptions(ArticleBySlug, {
  mainContentClass: 'Article',
  mapping: {
    'Author': ['authorPath', 'author']
  },
  pathKey: 'slug'
});

ArticleBySlug.translatedAttributes = {
  title: {
    path: ['title'],
    translationSource: {
      contentPath: ['$path'],
      translationAttr: 'title',
    },
  },
  introduction: {
    path: ['introduction'],
    translationSource: {
      contentPath: ['$path'],
      translationAttr: 'introduction',
    },
  },
  body: {
    path: ['body'],
    translationSource: {
      contentPath: ['$path'],
      translationAttr: 'body',
    },
  },
};


export class FactionSettingsIndexer extends Indexer {

  itemData (content) {
    return content.data;
  }

  get linkedContentPaths () {
    let pathList = new Set();
    for (let item of this.items.toJS()) {
      for (let unitReveal of item.unitReveals) {
        pathList.add(unitReveal.imagePath);
      }
      for (let {src, formats, widths, heights} of item.imageCarousel) {
        for (let {fileExt} of (formats|| [{contentType: "image/jpeg", fileExt: "jpg"}])) {
          for ( let width of (widths || []) ) {
            pathList.add(formattedImgPath(src, {width}, fileExt));
          }
          for (let height of (heights || [])) {
            pathList.add(formattedImgPath(src, {height}, fileExt));
          }
        }
      }
    }
    return Array.from(pathList);
  }
}

FactionSettingsIndexer.contentClassName = 'FactionSettings';

FactionSettingsIndexer.translatedAttributes = {
  abilityTitle: {
    *path (content) {
      for (let [itemIdx, item] of content.data.get('items').entries()) {
        for (let [abilityIdx, ability] of item.get('abilities').entries()) {
          yield [['items', itemIdx, 'abilities', abilityIdx], ability];
        }
      }
    },
    values: ['title'],
    async translationSource (pathToAbility, ability, repo, content) {
      let [items, itemIdx, ...abilityPath] = pathToAbility;
      let sourceContent = await repo.fetchContentAtPath(getAt(
        content.data, [items, itemIdx, 'path']));
      return sourceContent.getMessageId(`abilityTitle.${getAt(sourceContent.data, abilityPath.concat(['mediaName']))}`);
    },
  },
  abilityDescription: {
    *path (content) {
      for (let [itemIdx, item] of content.data.get('items').entries()) {
        for (let [abilityIdx, ability] of item.get('abilities').entries()) {
          yield [['items', itemIdx, 'abilities', abilityIdx], ability];
        }
      }
    },
    values: ['description'],
    async translationSource (pathToAbility, ability, repo, content) {
      let [items, itemIdx, ...abilityPath] = pathToAbility;
      let sourceContent = await repo.fetchContentAtPath(getAt(
        content.data, [items, itemIdx, 'path']));
      return sourceContent.getMessageId(`abilityDescription.${getAt(sourceContent.data, abilityPath.concat(['mediaName']))}`);
    },
  },
  unitRevealTitle: {
    *path (content) {
      for (let [itemIdx, item] of content.data.get('items').entries()) {
        for (let [revealIdx, reveal] of item.get('unitReveals').entries()) {
          yield [['items', itemIdx, 'unitReveals', revealIdx], reveal];
        }
      }
    },
    values: ['title'],
    async translationSource (pathToReveal, reveal, repo, content) {
      let [items, itemIdx, ...revealPath] = pathToReveal;
      let sourceContent = await repo.fetchContentAtPath(getAt(
        content.data, [items, itemIdx, 'path']));
      return sourceContent.getMessageId(`unitRevealTitle.${getAt(sourceContent.data, revealPath.concat(['articleSlug']))}`);
    },
  },
  imageTitle: {
    *path (content) {
      for (let [itemIdx, item] of content.data.get('items').entries()) {
        for (let [imgIdx, img] of item.get('imageCarousel').entries()) {
          yield [['items', itemIdx, 'imageCarousel', imgIdx], img];
        }
      }
    },
    values: ['title'],
    async translationSource (pathToImg, img, repo, content) {
      let [items, itemIdx, ...imgPath] = pathToImg;
      let sourceContent = await repo.fetchContentAtPath(getAt(
        content.data, [items, itemIdx, 'path']));
      return sourceContent.getMessageId(`imageTitle.${getAt(sourceContent.data, imgPath.concat(['imagePath']))}`);
    },
  },
};

export class MediaIndexer extends Indexer {

  get linkedContentPaths () {
    let pathList = [];
    for (let item of this.items.toJS()) {
      pathList.push(item.thumbnailPath);
      if (typeof (item.devices) !== 'undefined') {
        for (let deviceName in item.devices) {
          for (let j = 0; j < item.devices[deviceName].length; j++) {
            pathList.push(item.devices[deviceName][j].imagePath);
          }
        }
      }
    }

    return pathList;
  }

  get sortKey () {
    return ['priority'];
  }

}

MediaIndexer.contentClassName = 'MediaItem';

MediaIndexer.translatedAttributes = {
  title: {
    path: ['items'],
    values: ['title'],
    translationSource: {
      contentPath: ['path'],
      translationAttr: 'title',
    },
  },
};

export class FactionSettingsByFaction extends Denormalizer {
  get linkedContentPaths () {
    let pathList = new Set();
    let item = this.data.toJS();
    for (let unitReveal of item.unitReveals) {
      pathList.add(unitReveal.imagePath);
    }
    for (let {src, formats, widths, heights} of item.imageCarousel) {
      for (let {fileExt} of (formats|| [{contentType: "image/jpeg", fileExt: "jpg"}])) {
        for ( let width of (widths || []) ) {
          pathList.add(formattedImgPath(src, {width}, fileExt));
        }
        for (let height of (heights || [])) {
          pathList.add(formattedImgPath(src, {height}, fileExt));
        }
      }
    }

    return Array.from(pathList);
  }

  static contentData (content) {
    return content.data.set('imageCarousel', content.imageCarousel).set('$path', content.path);
  }
}

mergeStaticOptions(FactionSettingsByFaction, {
  mainContentClass: 'FactionSettings',
  mapping: {},
  pathKey: 'factionId'
});

FactionSettingsByFaction.translatedAttributes = {
  abilityTitle: {
    path: ['abilities'],
    values: ['title'],
    async translationSource (pathToAbility, ability, repo, content) {
      let sourceContent = await repo.fetchContentAtPath(getAt(content.data, ['$path']));
      return sourceContent.getMessageId(`abilityTitle.${getAt(sourceContent.data, pathToAbility.concat(['mediaName']))}`);
    },
  },
  abilityDescription: {
    path: ['abilities'],
    values: ['description'],
    async translationSource (pathToAbility, ability, repo, content) {
      let sourceContent = await repo.fetchContentAtPath(getAt(content.data, ['$path']));
      return sourceContent.getMessageId(`abilityDescription.${getAt(sourceContent.data, pathToAbility.concat(['mediaName']))}`);
    },
  },
  unitRevealTitle: {
    path: ['unitReveals'],
    values: ['title'],
    async translationSource (pathToReveal, ability, repo, content) {
      let sourceContent = await repo.fetchContentAtPath(getAt(content.data, ['$path']));
      return sourceContent.getMessageId(`unitRevealTitle.${getAt(sourceContent.data, pathToReveal.concat(['articleSlug']))}`);
    },
  },
  imageTitle: {
    path: ['imageCarousel'],
    values: ['title'],
    async translationSource (pathToImg, ability, repo, content) {
      let sourceContent = await repo.fetchContentAtPath(getAt(content.data, ['$path']));
      return sourceContent.getMessageId(`imageTitle.${getAt(sourceContent.data, pathToImg.concat(['imagePath']))}`);
    },
  },
};

export class ShopIndexer extends Indexer {

  itemData (content) {
    return content.data;
  }

  get linkedContentPaths () {
    let pathList = [];
    for (let item of this.data.get('items')) {
      pathList.push(item.get('backgroundPath'));
      pathList.push(item.get('squareBackgroundPath'));
      pathList.push(item.get('modalBackgroundPath'));
      pathList.push(item.get('esrbIconPath'));
    }
    return pathList;
  }

  get sortKey () {
    return ['priority'];
  }
}

ShopIndexer.contentClassName = 'ShopItem';

export class ShopItemBySlug extends Denormalizer {
  itemData (content) {
    return content.data;
  }

  get linkedContentPaths () {
    let pathList = [];
    for (let item of this.data.get('items')) {
      pathList.push(item.get('backgroundPath'));
      pathList.push(item.get('squareBackgroundPath'));
      pathList.push(item.get('modalBackgroundPath'));
      pathList.push(item.get('esrbIconPath'));
    }
    return pathList;
  }
}

mergeStaticOptions(ShopItemBySlug, {
  mainContentClass: 'ShopItem',
  mapping: {},
  pathKey: 'slug'
});

export default async function (command, repo) {
  async function getAllProcessImageInstances() {
    return [].concat(await getAllFactionSettings(), await getAllArticles(), await getAllTwitchChannelThumbnails());
  }

  async function getAllFactionSettings() {
    let instances = [];
    for (let promise of  await repo.mapPaths(`/content/factionsettings/`,
                                path => repo.fetchContentAtPath(path)) ) {
      instances.push(await promise);
    }
    return instances;
  }

  async function getAllArticles() {
    let instances = [];
    for (let promise of  await repo.mapPaths(`/content/article/`,
                                path => repo.fetchContentAtPath(path)) ) {
      instances.push(await promise);
    }

    return instances;
  }

  async function getAllTwitchChannelThumbnails () {
    let instances = [];
    let twitchConfig = await repo.fetchContentAtPath('/content/twitch-config.json');
    for (let group of twitchConfig.whitelistGroups) {
      for (let channel of group.get('whitelistChannels')) {
        let thumbnail = channel.get('thumbnail');
        if (thumbnail) {
          instances.push(thumbnail);
        }
      }
    }

    return instances;
  }

  await repo.submit(
    await ArticlesIndexer.index(command, repo),
    await MediaIndexer.index(command, repo),
    await FactionSettingsIndexer.index(command, repo),
    await ShopIndexer.index(command, repo),
    ... await processImages(command, repo, getAllProcessImageInstances),
    ... await ArticleBySlug.index(command, repo),
    ... await FactionSettingsByFaction.index(command, repo),
    ... await ShopItemBySlug.index(command, repo)
  );
}



// WEBPACK FOOTER //
// ./apps/www.dawnofwar.com/src/indexers.js